/**
 * Singleton WebSocket client (PRD-18 §FR-1, §FR-2).
 *
 * One open connection per tab, no matter how many components subscribe.
 * The client:
 *   - opens with the current JWT (from a caller-provided getter so we
 *     always read the latest token rather than a stale capture),
 *   - dispatches typed envelopes to subscribers via an event bus,
 *   - reconnects with exponential backoff (1s → 30s),
 *   - tracks status (`connecting`/`open`/`reconnecting`/`closed`) and
 *     notifies status listeners,
 *   - emits a synthetic `realtime.connected` event after every
 *     successful (re)connect so consumers can reconcile by re-fetching.
 *
 * Designed to live outside React. `RealtimeProvider` wires it to the
 * Auth context; hooks consume the bus.
 */

import { logger } from "@lib/logger"

import type {
  RealtimeEnvelope,
  RealtimeEventPayload,
  RealtimeEventType,
  RealtimeStatus,
} from "./types"

type Handler<T extends RealtimeEventType> = (payload: RealtimeEventPayload[T]) => void

type StatusListener = (status: RealtimeStatus) => void

// Backoff schedule (ms). Capped at 30s per PRD-18 §FR-1.7.
const BACKOFF_SCHEDULE_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000]

// If we don't hear from the server in this window, the socket is
// assumed dead — close and force a reconnect.
const HEARTBEAT_GRACE_MS = 60_000

export interface RealtimeClient {
  // Async so the provider can refresh an expired JWT BEFORE the
  // handshake: the token travels in the URL and an expired one is
  // rejected with HTTP 403 pre-upgrade, which the browser can only
  // report as close(1006) — retrying it blindly is an unauthorized-
  // handshake loop the server logs as an auth failure every 30s.
  start: (getToken: () => Promise<string | null>, buildUrl: () => string) => void
  stop: () => void
  subscribe: <T extends RealtimeEventType>(eventType: T, handler: Handler<T>) => () => void
  onStatus: (listener: StatusListener) => () => void
  getStatus: () => RealtimeStatus
}

function createRealtimeClient(): RealtimeClient {
  // Per-event subscribers. Sets so unsubscribe is O(1) and the same
  // handler reference can't double-register accidentally.
  const handlers = new Map<RealtimeEventType, Set<Handler<RealtimeEventType>>>()
  const statusListeners = new Set<StatusListener>()

  let ws: WebSocket | null = null
  let status: RealtimeStatus = "closed"
  let getToken: (() => Promise<string | null>) | null = null
  let buildUrl: (() => string) | null = null
  // null = we WANT to be connected; non-null = we asked to stop.
  let stopped = true
  let reconnectAttempt = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null
  // Bumped by stop()/start(); an open() that awaited its token across a
  // generation change must abandon instead of resurrecting a connection
  // the caller no longer wants.
  let generation = 0

  function setStatus(next: RealtimeStatus) {
    if (status === next) return
    status = next
    statusListeners.forEach((l) => {
      try {
        l(next)
      } catch (err) {
        logger.error("realtime.status_listener_failed", { error: String(err) })
      }
    })
  }

  function dispatch(envelope: RealtimeEnvelope) {
    const set = handlers.get(envelope.type)
    if (!set || set.size === 0) return
    set.forEach((handler) => {
      try {
        handler(envelope.payload)
      } catch (err) {
        logger.error("realtime.handler_failed", {
          event_type: envelope.type,
          error: String(err),
        })
      }
    })
  }

  function armHeartbeat() {
    if (heartbeatTimer) clearTimeout(heartbeatTimer)
    heartbeatTimer = setTimeout(() => {
      logger.warning("realtime.heartbeat_timeout")
      // Force-close; onclose triggers the reconnect path.
      ws?.close()
    }, HEARTBEAT_GRACE_MS)
  }

  function clearHeartbeat() {
    if (heartbeatTimer) {
      clearTimeout(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  function scheduleReconnect() {
    if (stopped) return
    setStatus("reconnecting")
    const delay = BACKOFF_SCHEDULE_MS[Math.min(reconnectAttempt, BACKOFF_SCHEDULE_MS.length - 1)]
    reconnectAttempt += 1
    if (reconnectTimer) clearTimeout(reconnectTimer)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      open()
    }, delay)
  }

  function open() {
    void openAsync()
  }

  async function openAsync() {
    if (stopped || !getToken || !buildUrl) return
    const myGeneration = generation
    setStatus(status === "closed" ? "connecting" : "reconnecting")
    // May refresh an expired JWT (auth/refreshAccess.ts) — this await is
    // the whole reason connect is async.
    const token = await getToken()
    // While we awaited, stop() or a fresh start() may have run.
    if (stopped || generation !== myGeneration) return
    if (!token) {
      // No usable token — caller re-invokes start() when one exists.
      setStatus("closed")
      return
    }
    const url = `${buildUrl()}?token=${encodeURIComponent(token)}`
    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch (err) {
      logger.error("realtime.ws_construct_failed", { error: String(err) })
      scheduleReconnect()
      return
    }
    ws = socket

    socket.addEventListener("open", () => {
      if (ws !== socket) return // superseded while connecting
      reconnectAttempt = 0
      setStatus("open")
      armHeartbeat()
      // The server's hello frame ('realtime.connected') is the canonical
      // "you're live" signal — we don't dispatch our own here.
    })

    socket.addEventListener("message", (event) => {
      if (ws !== socket) return
      armHeartbeat()
      let envelope: RealtimeEnvelope
      try {
        envelope = JSON.parse(event.data as string) as RealtimeEnvelope
      } catch (err) {
        logger.error("realtime.message_parse_failed", { error: String(err) })
        return
      }
      if (!envelope || typeof envelope.type !== "string") return
      dispatch(envelope)
    })

    socket.addEventListener("close", (event) => {
      // A stale socket's close must not tear down its replacement —
      // possible now that connects await the token asynchronously.
      if (ws !== socket) return
      clearHeartbeat()
      ws = null
      // 4401 = JWT rejected. Don't hammer the server — wait for a
      // fresh token. The auth provider re-calls start() when it has
      // one (token refresh or new login).
      if (event.code === 4401) {
        logger.warning("realtime.close_unauthorized", { code: event.code })
        setStatus("closed")
        return
      }
      logger.info("realtime.close", { code: event.code, clean: event.wasClean })
      scheduleReconnect()
    })

    socket.addEventListener("error", () => {
      // The 'error' event is followed by 'close'. Logging both would
      // double-fire; reconnect logic lives in onclose.
      if (ws === socket) logger.warning("realtime.socket_error")
    })
  }

  /**
   * Reconnect NOW if we want to be connected and aren't — the page just
   * became visible / came back online / returned from bfcache. Skips the
   * backoff: those events are positive evidence the network path exists,
   * and the professor is looking at the screen waiting for live state.
   */
  function kick(reason: string) {
    if (stopped) return
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return
    logger.info("realtime.kick", { reason })
    reconnectAttempt = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    open()
  }

  /**
   * The page is being suspended (frozen tab, bfcache navigation, laptop
   * lid). Close cleanly with 1000 so the server records an intentional
   * goodbye instead of waiting ~40s of missed pings to reap a mute
   * socket as 1006 — which is exactly what staging logs were full of
   * (uvicorn's ping 20s + timeout 20s vs sleeping residential clients).
   * `stopped` stays false: kick() reconnects on resume.
   */
  function suspend(reason: string) {
    if (stopped || !ws) return
    logger.info("realtime.suspend", { reason })
    clearHeartbeat()
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    const socket = ws
    ws = null // detach first so the close handler doesn't schedule a reconnect
    try {
      socket.close(1000, reason)
    } catch {
      // ignored — socket may already be closing
    }
    setStatus("closed")
  }

  // Page Lifecycle wiring. The browser suspends timers and JS in hidden
  // or frozen tabs, so this client cannot rely on its backoff timer to
  // survive a suspend — it has to close before the freeze and reconnect
  // on the wake-up signals.
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") kick("visible")
    })
    window.addEventListener("online", () => kick("online"))
    window.addEventListener("pageshow", (event) => {
      if ((event as PageTransitionEvent).persisted) kick("bfcache-restore")
    })
    // Chrome fires `freeze` before suspending a background tab; `resume`
    // when it thaws. Safari/Firefox don't, but they also fire pagehide/
    // visibilitychange on the paths that matter.
    document.addEventListener("freeze", () => suspend("page-frozen"))
    document.addEventListener("resume", () => kick("page-resumed"))
    window.addEventListener("pagehide", (event) => {
      // persisted → bfcache: the socket would be dead on restore anyway;
      // close cleanly and let pageshow reconnect. Non-persisted → the
      // page is being torn down; a clean close beats a 1006 either way.
      suspend(event.persisted ? "bfcache-enter" : "page-hidden")
    })
  }

  function start(getTokenFn: () => Promise<string | null>, buildUrlFn: () => string) {
    getToken = getTokenFn
    buildUrl = buildUrlFn
    stopped = false
    generation += 1
    reconnectAttempt = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    // If a connection is already open, do nothing — multiple start()
    // calls during the same session must be idempotent.
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return
    }
    open()
  }

  function stop() {
    stopped = true
    generation += 1
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    clearHeartbeat()
    if (ws) {
      try {
        ws.close(1000, "client-stop")
      } catch {
        // ignored — socket may already be closing
      }
      ws = null
    }
    setStatus("closed")
  }

  function subscribe<T extends RealtimeEventType>(eventType: T, handler: Handler<T>) {
    let set = handlers.get(eventType)
    if (!set) {
      set = new Set()
      handlers.set(eventType, set)
    }
    set.add(handler as Handler<RealtimeEventType>)
    return () => {
      set?.delete(handler as Handler<RealtimeEventType>)
      if (set && set.size === 0) handlers.delete(eventType)
    }
  }

  function onStatus(listener: StatusListener) {
    statusListeners.add(listener)
    // Replay the current status so newly-mounted hooks don't have to
    // wait for the next transition.
    try {
      listener(status)
    } catch {
      // ignored — listener errors must not break subscription
    }
    return () => {
      statusListeners.delete(listener)
    }
  }

  function getStatus() {
    return status
  }

  return { start, stop, subscribe, onStatus, getStatus }
}

export const realtimeClient = createRealtimeClient()
